import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { CopySimple, PencilSimple, Plus } from "@phosphor-icons/react";
import { SectionItem, SectionWithItem } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
} from "@reactive-resume/ui";
import { produce } from "immer";
import get from "lodash.get";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { DialogName, useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

type Props<T extends SectionItem> = {
  id: DialogName;
  form: UseFormReturn<T>;
  defaultValues: T;
  pendingKeyword?: string;
  children: React.ReactNode;
};

const getDialogItemLabel = (id: DialogName, sectionName?: string) => {
  if (typeof id === "string" && id.startsWith("custom.")) {
    return sectionName || "Section";
  }

  const labels: Partial<Record<DialogName, string>> = {
    awards: "Achievement",
    basics: "Basics",
    certifications: "Certification",
    education: "Education",
    experience: "Experience",
    extracurriculars: "Extra-curricular",
    interests: "Interest",
    internship: "Internship",
    languages: "Language",
    profiles: "Profile",
    projects: "Project",
    publications: "Publication",
    references: "Reference",
    skills: "Skill",
    summary: "Summary",
    volunteer: "Social Impact",
  };

  return labels[id] || sectionName || "Item";
};

export const SectionDialog = <T extends SectionItem>({
  id,
  form,
  defaultValues,
  pendingKeyword,
  children,
}: Props<T>) => {
  const { isOpen, mode, close, payload } = useDialog<T>(id);

  const setValue = useResumeStore((state) => state.setValue);
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const section = useResumeStore((state) => {
    if (!id) return null;
    return get(state.resume.data?.sections, id);
  }) as SectionWithItem<T> | null;
  const isMastersUnion = template === "mastersunion";

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";
  const dialogItemLabel = getDialogItemLabel(id, section?.name);

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen, payload]);

  const onSubmit = async (values: T) => {
    if (!section) return;
    const nextValues = { ...values } as T & { name?: string; keywords?: string[] };

    if (id === "projects" && isMastersUnion && "name" in nextValues && !nextValues.name?.trim()) {
      nextValues.name = t`Project`;
    }

    if (isCreate || isDuplicate) {
      if (pendingKeyword && "keywords" in nextValues && nextValues.keywords) {
        nextValues.keywords.push(pendingKeyword);
      }

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          draft.push({ ...nextValues, id: createId() });
        }),
      );
    }

    if (isUpdate) {
      if (!payload.item?.id) return;

      if (pendingKeyword && "keywords" in nextValues && nextValues.keywords) {
        nextValues.keywords.push(pendingKeyword);
      }

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === payload.item?.id);
          if (index === -1) return;
          draft[index] = nextValues;
        }),
      );
    }

    if (isDelete) {
      if (!payload.item?.id) return;

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === payload.item?.id);
          if (index === -1) return;
          draft.splice(index, 1);
        }),
      );
    }

    close();
  };

  const onReset = () => {
    type ResettableItem = Record<string, unknown> & {
      id?: string;
      name?: string;
      institution?: unknown;
      company?: unknown;
    };

    const convertStringToUrl = (value: unknown) => {
      if (typeof value === "string" && value.trim()) {
        return { label: value.trim(), href: value.trim() };
      }
      if (
        value &&
        typeof value === "object" &&
        "label" in value &&
        "href" in value &&
        typeof value.label === "string" &&
        typeof value.href === "string"
      ) {
        return value;
      }
      return { label: "", href: "" };
    };

    const migrateCustomSectionCompanyFromName = (item: ResettableItem) => {
      const nameStr = typeof item.name === "string" ? item.name.trim() : "";
      const company = item.company as { label?: string; href?: string } | undefined;
      const url = item.url as { href?: string } | undefined;
      const cl = (company?.label ?? "").trim();
      const ch = (company?.href ?? "").trim();
      if (nameStr && !cl && !ch) {
        item.company = { label: nameStr, href: (url?.href ?? "").trim() };
      }
    };

    if (isCreate) {
      form.reset({ ...defaultValues, id: createId() } as T);
      if (id === "projects" && isMastersUnion) {
        form.setValue("name" as never, t`Project` as never, { shouldValidate: true });
      }
    }
    if (isUpdate && payload.item) {
      const item = { ...(payload.item as ResettableItem) };

      // Convert legacy string values to URL objects for education, experience, and internship
      if (id === "education" && item.institution) {
        item.institution = convertStringToUrl(item.institution);
      }
      if ((id === "experience" || id === "internship") && item.company) {
        item.company = convertStringToUrl(item.company);
      }
      if (typeof id === "string" && id.startsWith("custom.")) {
        migrateCustomSectionCompanyFromName(item);
        if (item.company) {
          item.company = convertStringToUrl(item.company);
        }
      }
      if (id === "projects" && isMastersUnion && !item.name) {
        item.name = t`Project`;
      }

      form.reset({ ...defaultValues, ...item } as T);
    }
    if (isDuplicate && payload.item) {
      const item: ResettableItem = { ...(payload.item as ResettableItem), id: createId() };
      // Convert legacy string values to URL objects for education, experience, and internship
      if (id === "education" && item.institution) {
        item.institution = convertStringToUrl(item.institution);
      }
      if ((id === "experience" || id === "internship") && item.company) {
        item.company = convertStringToUrl(item.company);
      }
      if (typeof id === "string" && id.startsWith("custom.")) {
        migrateCustomSectionCompanyFromName(item);
        if (item.company) {
          item.company = convertStringToUrl(item.company);
        }
      }
      if (id === "projects" && isMastersUnion && !item.name) {
        item.name = t`Project`;
      }
      form.reset({ ...defaultValues, ...item } as T);
    }
    if (isDelete && payload.item) {
      const item = { ...(payload.item as ResettableItem) };
      // Convert legacy string values to URL objects for education, experience, and internship
      if (id === "education" && item.institution) {
        item.institution = convertStringToUrl(item.institution);
      }
      if ((id === "experience" || id === "internship") && item.company) {
        item.company = convertStringToUrl(item.company);
      }
      if (typeof id === "string" && id.startsWith("custom.")) {
        migrateCustomSectionCompanyFromName(item);
        if (item.company) {
          item.company = convertStringToUrl(item.company);
        }
      }
      if (id === "projects" && isMastersUnion && !item.name) {
        item.name = t`Project`;
      }
      form.reset({ ...defaultValues, ...item } as T);
    }
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent className="z-50">
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete this item?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action can be reverted by clicking on the undo button in the floating toolbar.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="z-50" 
      // style={{transform: 'unset', height: '100vh', overflow: 'auto', top: '0px', left: 'unset', right: '0px'}}
      >
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  {isCreate && !isMastersUnion && <Plus />}
                  {isUpdate && <PencilSimple />}
                  {isDuplicate && <CopySimple />}
                  <h2>
                    {isCreate &&
                      (isMastersUnion ? `Create a new ${dialogItemLabel}` : t`Create a new item`)}
                    {isUpdate &&
                      (isMastersUnion
                        ? `Update an existing ${dialogItemLabel}`
                        : t`Update an existing item`)}
                    {isDuplicate &&
                      (isMastersUnion
                        ? `Duplicate an existing ${dialogItemLabel}`
                        : t`Duplicate an existing item`)}
                  </h2>
                </div>
              </DialogTitle>
            </DialogHeader>

            {children}

            <DialogFooter>
              <Button
                type="submit"
                // disabled={
                //   isCreate && !(form.getValues() as { name?: string })?.name
                // }
              >
                {isCreate && t`Create`}
                {isUpdate && t`Save Changes`}
                {isDuplicate && t`Duplicate`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
